import { z } from 'zod';
import { clearMiniQuizResumeState } from '../navigation/miniQuizResume/storage';
import { type Quiz } from './quiz';

const twoWeeks = 14 * 24 * 60 * 60 * 1000;

export type QuizStorageNamespace = 'quiz' | 'mini-quiz';

export const DEFAULT_QUIZ_STORAGE_NAMESPACE = 'quiz' as const;
export const MINI_QUIZ_STORAGE_NAMESPACE = 'mini-quiz' as const;

/**
 * Which funnel the visitor most recently stepped into. Lives outside both
 * `quiz.*` and `mini-quiz.*` because it outlives either funnel's progress: a
 * guest's answers move to the server at signup and the local progress is
 * cleared, but their CTAs must still lead back to the funnel they started.
 *
 * Written by each funnel's nav tracking, read first by funnel entry points.
 * Self-expiring (see {@link getStartedFunnel}) rather than cleared by the
 * funnels, so nothing has to coordinate and the key is inert if a funnel is
 * ever removed.
 */
export const STARTED_FUNNEL_STORAGE_KEY = 'funnel.started';

const startedFunnelSchema = z.object({
  funnel: z.enum([DEFAULT_QUIZ_STORAGE_NAMESPACE, MINI_QUIZ_STORAGE_NAMESPACE]),
  timestamp: z.coerce.date(),
});

export function getQuizStorageKeys(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  return {
    local: `${namespace}.local`,
    nav: `${namespace}.nav`,
    expiry: `${namespace}.expiry`,
    entry: `${namespace}.entry`,
    completed: `${namespace}.completed`,
  };
}

export const LOCAL_STORAGE_KEY = getQuizStorageKeys().local;
export const QUIZ_NAVIGATION_LOCAL_STORAGE_KEY = getQuizStorageKeys().nav;
export const QUIZ_EXPIRY_LOCAL_STORAGE_KEY = getQuizStorageKeys().expiry;
export const QUIZ_RESET_TTL = twoWeeks;

export const getQuizLocalStorageExpiry = (ttl: number) =>
  new Date(Date.now() + ttl).toISOString();

export function isQuizLocalStorageExpired(
  expiry: string | null,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  if (expiry === null) {
    // Fallback - set the expiry date if it doesn't exist
    // This would be the case for users who have existing quiz local storage data from prior to rolling out this FE quiz reset.
    // A week after this initial, we can update this logic to simply clear local storage if expiry is null. Those users have not returned before the reset TTL and should start with empty quiz local storage.
    window.localStorage.setItem(
      getQuizStorageKeys(namespace).expiry,
      JSON.stringify(getQuizLocalStorageExpiry(QUIZ_RESET_TTL))
    );
    return false;
  }

  const now = new Date();
  const isExpired = new Date(JSON.parse(expiry)) < now;
  return isExpired;
}

export function clearQuizProgressLocalStorage(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  const keys = getQuizStorageKeys(namespace);

  if (window.localStorage.getItem(keys.local) !== null) {
    window.localStorage.removeItem(keys.local);
  }
  if (window.localStorage.getItem(keys.expiry) !== null) {
    window.localStorage.removeItem(keys.expiry);
  }
  if (window.localStorage.getItem(keys.nav) !== null) {
    window.localStorage.removeItem(keys.nav);
  }
  if (window.localStorage.getItem(keys.completed) !== null) {
    window.localStorage.removeItem(keys.completed);
  }
}

/**
 * Marks the funnel's quiz as completed: the visitor reached its signup
 * hand-off. A bare flag beside `.local` — it carries no clock of its own and
 * lives exactly as long as the answers do (`.expiry`, cleared with them by
 * {@link clearQuizProgressLocalStorage}). Deliberately separate from `.nav`
 * and `mini-quiz.resume`, whose 24h resume windows are a CTA decision, not
 * the lifetime of the answers.
 */
export function markQuizCompleted(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): void {
  try {
    window.localStorage.setItem(
      getQuizStorageKeys(namespace).completed,
      new Date().toISOString()
    );
  } catch {
    // localStorage can throw in private mode; completion is simply unknown.
  }
}

/** Whether {@link markQuizCompleted} has been recorded for the funnel. */
export function hasCompletedQuiz(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
): boolean {
  try {
    return (
      window.localStorage.getItem(getQuizStorageKeys(namespace).completed) !==
      null
    );
  } catch {
    return false;
  }
}

export function clearQuizLocalStorage(
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  clearQuizProgressLocalStorage(namespace);

  if (namespace === MINI_QUIZ_STORAGE_NAMESPACE) {
    clearMiniQuizResumeState();
  }

  const keys = getQuizStorageKeys(namespace);

  if (window.localStorage.getItem(keys.entry) !== null) {
    window.localStorage.removeItem(keys.entry);
  }
}

/**
 * Records `funnel` as the one the visitor is in. Each write refreshes the
 * timestamp, so an active visitor never ages out; a visitor who steps into
 * the other funnel simply overwrites it.
 */
export function saveStartedFunnel(funnel: QuizStorageNamespace): void {
  try {
    window.localStorage.setItem(
      STARTED_FUNNEL_STORAGE_KEY,
      JSON.stringify({ funnel, timestamp: new Date() })
    );
  } catch {
    // localStorage can throw in private mode; entry falls back to defaults.
  }
}

/**
 * The funnel the visitor started, or `undefined` when none is recorded or the
 * record is older than {@link QUIZ_RESET_TTL} — the same window after which
 * the full quiz discards its own progress.
 */
export function getStartedFunnel(): QuizStorageNamespace | undefined {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STARTED_FUNNEL_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const validated = startedFunnelSchema.safeParse(parsed);
  if (!validated.success) {
    return undefined;
  }

  const ageMs = Date.now() - validated.data.timestamp.getTime();
  if (ageMs > QUIZ_RESET_TTL) {
    return undefined;
  }

  return validated.data.funnel;
}

function isInternalPath(value: string): boolean {
  return (
    value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\')
  );
}

export function saveMiniQuizEntryPoint(path: string): void {
  if (!isInternalPath(path)) {
    return;
  }

  window.localStorage.setItem(
    getQuizStorageKeys(MINI_QUIZ_STORAGE_NAMESPACE).entry,
    JSON.stringify(path)
  );
}

export function getSavedMiniQuizEntryPoint(): string | null {
  const raw = window.localStorage.getItem(
    getQuizStorageKeys(MINI_QUIZ_STORAGE_NAMESPACE).entry
  );
  if (raw === null) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string' && isInternalPath(parsed)) {
      return parsed;
    }
  } catch {
    if (isInternalPath(raw)) {
      return raw;
    }
  }

  return null;
}

export function saveQuizToLocalStorage(
  quiz: Quiz,
  namespace: QuizStorageNamespace = DEFAULT_QUIZ_STORAGE_NAMESPACE
) {
  const keys = getQuizStorageKeys(namespace);

  window.localStorage.setItem(keys.local, JSON.stringify(quiz));
  window.localStorage.setItem(
    keys.expiry,
    JSON.stringify(getQuizLocalStorageExpiry(QUIZ_RESET_TTL))
  );
}
